import { useTranslation } from 'react-i18next';
import type { AuditLogSearchCondition, ActionCatalogItem } from '../types/auditLog';

interface AuditLogFilterBarProps {
  filters: AuditLogSearchCondition;
  actions: ActionCatalogItem[];
  onFiltersChange: (partial: Partial<AuditLogSearchCondition>) => void;
  onSearch: () => void;
  onReset: () => void;
  isLoading?: boolean;
}

const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  fontWeight: 500,
  marginBottom: 'var(--spacing-1)',
  display: 'block',
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  whiteSpace: 'nowrap',
};

const btnSecondary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  whiteSpace: 'nowrap',
};

/**
 * AuditLogFilterBar — 액션/결과/심각도/자원/사용자/날짜/TraceID 필터 바
 * 반응형: md(768px) 이상 3컬럼, 이하 단일 컬럼
 */
export function AuditLogFilterBar({
  filters,
  actions,
  onFiltersChange,
  onSearch,
  onReset,
  isLoading,
}: AuditLogFilterBarProps) {
  const { t } = useTranslation();
  return (
    <div
      role="search"
      aria-label={t('auditLog.list.filterAriaLabel')}
      style={{
        padding: 'var(--spacing-4)',
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        marginBottom: 'var(--spacing-4)',
      }}
    >
      {/* 행 1: 액션 / 결과 / 심각도 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 'var(--spacing-3)',
          marginBottom: 'var(--spacing-3)',
        }}
      >
        <div>
          <label htmlFor="filter-action" style={labelStyle}>{t('auditLog.list.filter.action')}</label>
          <select
            id="filter-action"
            value={filters.action ?? ''}
            onChange={(e) => onFiltersChange({ action: e.target.value || undefined })}
            style={inputStyle}
          >
            <option value="">{t('auditLog.list.filter.actionAll')}</option>
            {actions.map((a) => (
              <option key={a.code} value={a.code}>
                {a.labelKo} ({a.code})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="filter-result" style={labelStyle}>{t('auditLog.list.filter.result')}</label>
          <select
            id="filter-result"
            value={filters.result ?? ''}
            onChange={(e) => onFiltersChange({ result: (e.target.value as AuditLogSearchCondition['result']) || undefined })}
            style={inputStyle}
          >
            <option value="">{t('auditLog.list.filter.actionAll')}</option>
            <option value="SUCCESS">{t('auditLog.result.SUCCESS')}</option>
            <option value="DENY">{t('auditLog.result.DENY')}</option>
            <option value="ERROR">{t('auditLog.result.ERROR')}</option>
          </select>
        </div>

        <div>
          <label htmlFor="filter-severity" style={labelStyle}>{t('auditLog.list.filter.severity')}</label>
          <select
            id="filter-severity"
            value={filters.severity ?? ''}
            onChange={(e) => onFiltersChange({ severity: (e.target.value as AuditLogSearchCondition['severity']) || undefined })}
            style={inputStyle}
          >
            <option value="">{t('auditLog.list.filter.actionAll')}</option>
            <option value="INFO">{t('auditLog.severity.INFO')}</option>
            <option value="NOTICE">{t('auditLog.severity.NOTICE')}</option>
            <option value="WARNING">{t('auditLog.severity.WARNING')}</option>
            <option value="ALERT">{t('auditLog.severity.ALERT')}</option>
            <option value="CRITICAL">{t('auditLog.severity.CRITICAL')}</option>
          </select>
        </div>
      </div>

      {/* 행 2: 자원 / 사용자 / Trace ID */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 'var(--spacing-3)',
          marginBottom: 'var(--spacing-3)',
        }}
      >
        <div>
          <label htmlFor="filter-resourceType" style={labelStyle}>{t('auditLog.list.filter.resourceType')}</label>
          <input
            id="filter-resourceType"
            type="text"
            placeholder={t('auditLog.list.filter.resourceTypePlaceholder')}
            value={filters.resourceType ?? ''}
            onChange={(e) => onFiltersChange({ resourceType: e.target.value || undefined })}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            style={inputStyle}
            aria-label={t('auditLog.list.filter.resourceType')}
          />
        </div>

        <div>
          <label htmlFor="filter-actor" style={labelStyle}>{t('auditLog.list.filter.actor')}</label>
          <input
            id="filter-actor"
            type="text"
            placeholder={t('auditLog.list.filter.actorPlaceholder')}
            value={filters.actor ?? ''}
            onChange={(e) => onFiltersChange({ actor: e.target.value || undefined })}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            style={inputStyle}
            aria-label={t('auditLog.list.filter.actor')}
          />
        </div>

        <div>
          <label htmlFor="filter-traceId" style={labelStyle}>{t('auditLog.list.filter.traceId')}</label>
          <input
            id="filter-traceId"
            type="text"
            placeholder={t('auditLog.list.filter.traceIdPlaceholder')}
            value={filters.traceId ?? ''}
            onChange={(e) => onFiltersChange({ traceId: e.target.value || undefined })}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            style={{ ...inputStyle, fontFamily: 'var(--font-family-mono)' }}
            aria-label={t('auditLog.list.filter.traceId')}
          />
        </div>
      </div>

      {/* 행 3: 기간 + 검색/초기화 버튼 */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
          alignItems: 'flex-end',
        }}
      >
        <div>
          <label htmlFor="filter-from" style={labelStyle}>{t('auditLog.list.filter.from')}</label>
          <input
            id="filter-from"
            type="date"
            value={filters.from ? filters.from.slice(0, 10) : ''}
            onChange={(e) =>
              onFiltersChange({
                from: e.target.value ? `${e.target.value}T00:00:00Z` : undefined,
              })
            }
            style={{ ...inputStyle, width: 'auto', minWidth: '140px' }}
            aria-label={t('auditLog.list.filter.from')}
          />
        </div>

        <span
          style={{
            paddingBottom: 'var(--spacing-2)',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
            alignSelf: 'flex-end',
          }}
          aria-hidden="true"
        >
          ~
        </span>

        <div>
          <label htmlFor="filter-to" style={labelStyle}>{t('auditLog.list.filter.to')}</label>
          <input
            id="filter-to"
            type="date"
            value={filters.to ? filters.to.slice(0, 10) : ''}
            onChange={(e) =>
              onFiltersChange({
                to: e.target.value ? `${e.target.value}T23:59:59Z` : undefined,
              })
            }
            style={{ ...inputStyle, width: 'auto', minWidth: '140px' }}
            aria-label={t('auditLog.list.filter.to')}
          />
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginLeft: 'auto' }}>
          <button
            type="button"
            onClick={onSearch}
            disabled={isLoading}
            style={{
              ...btnPrimary,
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
            aria-label={t('auditLog.list.button.search')}
          >
            {t('auditLog.list.button.search')}
          </button>
          <button
            type="button"
            onClick={onReset}
            style={btnSecondary}
            aria-label={t('auditLog.list.button.reset')}
          >
            {t('auditLog.list.button.reset')}
          </button>
        </div>
      </div>
    </div>
  );
}
